import { hotelCampaignRegistrationStatuses, hotelStatusNamesFilterable } from 'main/hotelStatuses'
import { parentChainNames, PMSnames } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { SecondaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import { getHealthCheckSyncData } from '../../../actions/healthCheck'
import { getHealthCheckBillingData } from '../../../actions/healthCheck'
import { getHealthCheckAutoInviteData } from '../../../actions/healthCheck'
import { getHealthCheckInvoices } from '../../../actions/healthCheck'
import CustomHeader from '../../../components/ReusableComponents/HeaderTitles'
import LoadingIndicator from '../../../components/ReusableComponents/LoadingIndicator'
import Table from '../../../components/ReusableComponents/Table'
import Tab from '../../../components/ReusableComponents/Tabs'
import Toggle from '../../../components/ReusableComponents/Toggle/index'
import audienceApi from '../../../services/api/audience'
import campaignApi from '../../../services/api/campaign'
import hotelApi from '../../../services/api/hotel'
import downloadAsCsv from '../../../utils/downloadAsCsv'
import HealthCheckFilters from '../Common/Filters'
import useHealthCheckFilters from '../Common/useHealthCheckFilters'
import { NoteBatchLoaderProvider, useNoteBatchLoader } from '../Common/NoteBatchLoaderContext'
import { ReactComponent as ExcelIcon } from '../Billing/excel.svg'
import { calculateFeatureStatuses } from './calculateFeatureStatuses'
import SummaryRow from './Row'
import styles from './Summary.module.scss'
import SummaryTitleRow from './TitleRow'

const HealthCheckSummary = () => {
  const dispatch = useDispatch()

  const [filterState, filterDispatch] = useHealthCheckFilters('summary')

  // Get data from all three sources
  const pmsLoading = useSelector(s => s.healthCheck.sync.isLoading)
  const pmsData = useSelector(s => s.healthCheck.sync.hotelData)

  const billingLoading = useSelector(s => s.healthCheck.billings.isLoading)
  const billingData = useSelector(s => s.healthCheck.billings.hotelData)

  const invitesLoading = useSelector(s => s.healthCheck.invites.isLoading)
  const invitesData = useSelector(s => s.healthCheck.invites.hotelData)

  const performanceLoading = useSelector(s => s.healthCheck.invoices.isLoading)
  const performanceData = useSelector(s => s.healthCheck.invoices.hotelData)

  const allhotelGroups = useSelector(s => s.healthCheck.sync.hotelGroups)
  const allhotelChains = useSelector(s => s.healthCheck.sync.hotelChains)

  const [sortByIndex, setSortByIndex] = useState(1)
  const [sortDecreasing, setSortDecreasing] = useState(false)
  const [displayLimit, setDisplayLimit] = useState(100) // Limit initial display to 100 rows

  // Alert status filter toggles - persisted in localStorage
  const { showAlert, showWarning, showGreen } = filterState

  // Feature status data map
  const [featureDataMap, setFeatureDataMap] = useState(new Map())
  const [isLoadingFeatures, setIsLoadingFeatures] = useState(false)

  // Campaigns data map
  const [campaignsMap, setCampaignsMap] = useState(new Map())
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false)

  // Custom campaigns (drip) count map
  const [customCampaignsCountMap, setCustomCampaignsCountMap] = useState(new Map())

  const isLoading = pmsLoading || billingLoading || invitesLoading || performanceLoading

  // Calculate row status based on business rules
  // Note: Autopay status (isAutoPayEnabled) is NOT considered in alert calculations
  const calculateRowStatus = row => {
    const {
      serviceStartDate,
      invited,
      outstandingInvoiceCount,
      marketingCampaigns,
      subsidiaryStatus,
      isAllReportHealthy,
      pmsSyncIsConnected,
      pmsSyncTotalReportsReceivedInLast48Hours,
      isHavingProblems
      // isAutoPayEnabled is intentionally NOT included - autopay status is not used for alert calculations
    } = row

    const textCount = invited?.periodCurrent || 0
    const textPrevious = invited?.periodPrevious || 0
    const oneWeekAgo = moment().subtract(1, 'week')
    const serviceStartedMoreThanWeekAgo =
      serviceStartDate && moment(serviceStartDate).isBefore(oneWeekAgo)

    // Check for significant reduction in text (> 30% drop)
    const hasSignificantTextReduction =
      textPrevious > 0 &&
      textCount < textPrevious &&
      (textPrevious - textCount) / textPrevious > 0.3

    // Check if PMS sync is working
    // PMS sync is working if: connected AND (all reports healthy OR has reports in last 48h) AND not having problems
    // If PMS sync data is not available, assume it's working (to avoid false alerts)
    const isPmsSyncWorking =
      pmsSyncIsConnected !== false &&
      (isAllReportHealthy === true ||
        (pmsSyncTotalReportsReceivedInLast48Hours !== undefined &&
          pmsSyncTotalReportsReceivedInLast48Hours > 0)) &&
      isHavingProblems !== true

    // Check invite status campaigns (3 Mo, 6 Mo, 9 Mo, 12 Mo, 15 Mo, 15 Mo+, After visit)
    const inviteStatusCampaignCategories = [
      'Lost3Months',
      'Lost6Months',
      'Lost9Months',
      'Lost12Months',
      'Lost15Months',
      'Lost15MonthsPlus',
      'AfterLastVisit'
    ]

    // Count how many invite status campaigns are disabled and calculate percentage
    let disabledInviteCampaignsCount = 0
    let totalInviteCampaigns = 0
    if (marketingCampaigns && marketingCampaigns.length > 0) {
      inviteStatusCampaignCategories.forEach(category => {
        const campaign = marketingCampaigns.find(c => c.category === category)
        if (campaign) {
          totalInviteCampaigns++
          if (!campaign.isActive) {
            disabledInviteCampaignsCount++
          }
        }
      })
    }

    const disabledCampaignsPercentage =
      totalInviteCampaigns > 0
        ? (disabledInviteCampaignsCount / totalInviteCampaigns) * 100
        : 0

    // Check if all features are disabled (all campaigns inactive)
    const allCampaignsInactive =
      marketingCampaigns &&
      marketingCampaigns.length > 0 &&
      marketingCampaigns.every(campaign => !campaign.isActive)

    // Check if hotel is active (not pending, deleted, or inactive)
    const isActiveHotel =
      subsidiaryStatus &&
      !['PendingCampaignBrandRegistration', 'Deleted', 'Inactive'].includes(
        subsidiaryStatus
      )

    // ALERT/HIGH (Red) conditions:
    // 1. Service Start Date > 1 week AND (no text OR PMS sync not working)
    if (serviceStartedMoreThanWeekAgo && (textCount === 0 || !isPmsSyncWorking)) {
      return 'alert'
    }

    // 2. All features disabled for active hotel
    if (isActiveHotel && allCampaignsInactive) {
      return 'alert'
    }

    // 3. Text count is 0 OR more than 1 pending invoice
    if (textCount === 0 || outstandingInvoiceCount > 1) {
      return 'alert'
    }

    // 4. Significant reduction in text (> 30% drop)
    if (hasSignificantTextReduction) {
      return 'alert'
    }

    // 5. More than 50% of invite status campaigns are disabled
    if (disabledCampaignsPercentage > 50) {
      return 'alert'
    }

    // WARNING conditions:
    // 1. 50-80% of invite status campaigns are disabled
    if (disabledCampaignsPercentage >= 50 && disabledCampaignsPercentage <= 80) {
      return 'warning'
    }

    // GREEN conditions:
    // Everything is good - service started, has text, PMS sync working (if service > 1 week),
    // < 50% invite campaigns disabled, invoices OK
    const hasText = textCount > 0
    const hasGoodInvoices = outstandingInvoiceCount <= 1
    const inviteCampaignsOk = disabledCampaignsPercentage < 50

    // If service started more than a week ago, PMS sync must be working
    const pmsSyncOk = !serviceStartedMoreThanWeekAgo || isPmsSyncWorking

    if (
      serviceStartDate &&
      hasText &&
      hasGoodInvoices &&
      inviteCampaignsOk &&
      pmsSyncOk
    ) {
      return 'green'
    }

    // WARNING (Yellow) - everything else
    return 'warning'
  }

  // Generate explanation for row status
  const getStatusExplanation = row => {
    const {
      serviceStartDate,
      invited,
      outstandingInvoiceCount,
      marketingCampaigns,
      subsidiaryStatus,
      isAllReportHealthy,
      pmsSyncIsConnected,
      pmsSyncTotalReportsReceivedInLast48Hours,
      isHavingProblems
    } = row

    const textCount = invited?.periodCurrent || 0
    const textPrevious = invited?.periodPrevious || 0
    const oneWeekAgo = moment().subtract(1, 'week')
    const serviceStartedMoreThanWeekAgo =
      serviceStartDate && moment(serviceStartDate).isBefore(oneWeekAgo)

    const hasSignificantTextReduction =
      textPrevious > 0 &&
      textCount < textPrevious &&
      (textPrevious - textCount) / textPrevious > 0.3

    const isPmsSyncWorking =
      pmsSyncIsConnected !== false &&
      (isAllReportHealthy === true ||
        (pmsSyncTotalReportsReceivedInLast48Hours !== undefined &&
          pmsSyncTotalReportsReceivedInLast48Hours > 0)) &&
      isHavingProblems !== true

    const inviteStatusCampaignCategories = [
      'Lost3Months',
      'Lost6Months',
      'Lost9Months',
      'Lost12Months',
      'Lost15Months',
      'Lost15MonthsPlus',
      'AfterLastVisit'
    ]

    let disabledCount = 0
    let totalCampaigns = 0
    if (marketingCampaigns && marketingCampaigns.length > 0) {
      inviteStatusCampaignCategories.forEach(category => {
        const campaign = marketingCampaigns.find(c => c.category === category)
        if (campaign) {
          totalCampaigns++
          if (!campaign.isActive) {
            disabledCount++
          }
        }
      })
    }

    const disabledCampaignsPercentage =
      totalCampaigns > 0 ? (disabledCount / totalCampaigns) * 100 : 0

    const allCampaignsInactive =
      marketingCampaigns &&
      marketingCampaigns.length > 0 &&
      marketingCampaigns.every(campaign => !campaign.isActive)

    const isActiveHotel =
      subsidiaryStatus &&
      !['PendingCampaignBrandRegistration', 'Deleted', 'Inactive'].includes(subsidiaryStatus)

    const status = calculateRowStatus(row)
    const reasons = []

    if (status === 'alert') {
      if (serviceStartedMoreThanWeekAgo && (textCount === 0 || !isPmsSyncWorking)) {
        if (textCount === 0) {
          reasons.push('No text sent')
        }
        if (!isPmsSyncWorking) {
          reasons.push('PMS sync not working')
        }
      }
      if (isActiveHotel && allCampaignsInactive) {
        reasons.push('All features disabled')
      }
      if (textCount === 0) {
        reasons.push('No text sent')
      }
      if (outstandingInvoiceCount > 1) {
        reasons.push(`${outstandingInvoiceCount} pending invoices`)
      }
      if (hasSignificantTextReduction) {
        const reductionPercent = ((textPrevious - textCount) / textPrevious * 100).toFixed(1)
        reasons.push(`Significant text reduction (${reductionPercent}% drop)`)
      }
      if (disabledCampaignsPercentage > 50) {
        reasons.push(`Features: ${(100 - disabledCampaignsPercentage).toFixed(0)}% enabled (${disabledCount}/${totalCampaigns} disabled, > 50% disabled)`)
      }
    } else if (status === 'warning') {
      if (disabledCampaignsPercentage >= 50 && disabledCampaignsPercentage <= 80) {
        reasons.push(`Features: ${(100 - disabledCampaignsPercentage).toFixed(0)}% enabled (${disabledCount}/${totalCampaigns} disabled, 50-80% disabled)`)
      }
    } else if (status === 'green') {
      reasons.push('All systems operational')
      if (textCount > 0) {
        reasons.push(`Text sent: ${textCount}`)
      }
      if (outstandingInvoiceCount <= 1) {
        reasons.push('Invoices up to date')
      }
      if (disabledCampaignsPercentage < 50) {
        reasons.push(`Features: ${(100 - disabledCampaignsPercentage).toFixed(0)}% enabled (${totalCampaigns - disabledCount}/${totalCampaigns} enabled, < 50% disabled)`)
      }
    }

    return reasons.length > 0 ? reasons.join(' • ') : 'Status: ' + status
  }

  // Fetch all data on mount and when invite period changes
  useEffect(() => {
    dispatch(getHealthCheckSyncData())
    dispatch(getHealthCheckBillingData())
    dispatch(getHealthCheckAutoInviteData(filterState.periodStart, filterState.periodEnd))
    dispatch(getHealthCheckInvoices())
  }, [dispatch, filterState.periodStart, filterState.periodEnd])

  // Fetch feature data for subsidiaries
  useEffect(() => {
    if (!pmsData || !Array.isArray(pmsData) || pmsData.length === 0) return

    const fetchFeatureData = async () => {
      setIsLoadingFeatures(true)

      // Extract unique subsidiary IDs (limit to first 200 for performance)
      const limitedPmsData = pmsData.slice(0, 200)
      const subsidiaryIds = [...new Set(limitedPmsData.map(row => row.subsidiaryId))]

      // Process in batches to avoid overwhelming the server
      const batchSize = 10
      const featureMap = new Map()

      for (let i = 0; i < subsidiaryIds.length; i += batchSize) {
        const batch = subsidiaryIds.slice(i, i + batchSize)

        try {
          const results = await Promise.allSettled(
            batch.map(subsidiaryId => hotelApi.getDetails(subsidiaryId))
          )

          results.forEach((result, index) => {
            if (result.status === 'fulfilled' && result.value) {
              const subsidiaryId = batch[index]
              const config = result.value

              // Debug logging for first few items
              if (i === 0 && index < 2) {
                console.log(`Feature data response for ${subsidiaryId}:`, config)
                console.log(`Config structure:`, {
                  hasSubsidiary: !!config?.subsidiary,
                  subsidiaryKeys: config?.subsidiary
                    ? Object.keys(config.subsidiary)
                    : [],
                  hasPhoneNumbers: !!config?.phoneNumbers,
                  hasSubsidiaryServices: !!config?.subsidiaryServices
                })
              }

              // Calculate feature statuses
              const featureStatuses = calculateFeatureStatuses(config)

              // Debug logging for first item
              if (i === 0 && index === 0) {
                console.log(`Calculated feature statuses:`, featureStatuses)
              }

              // Get actualServiceStartDate from config
              const actualServiceStartDate =
                config?.subsidiary?.actualServiceStartDate || null

              featureMap.set(subsidiaryId, {
                featureStatuses,
                actualServiceStartDate
              })
            } else if (result.status === 'rejected') {
              console.warn(
                `Failed to fetch feature data for subsidiary ${batch[index]}:`,
                result.reason
              )
            }
          })
        } catch (error) {
          console.warn('Error fetching feature data batch:', error)
        }
      }

      setFeatureDataMap(featureMap)
      setIsLoadingFeatures(false)
    }

    fetchFeatureData()
  }, [pmsData])

  // Fetch regular campaigns data (for CampaignStatusCell - Sales/Service)
  useEffect(() => {
    if (!pmsData || !Array.isArray(pmsData) || pmsData.length === 0) return

    const fetchCampaigns = async () => {
      setIsLoadingCampaigns(true)

      const limitedPmsData = pmsData.slice(0, 200)
      const subsidiaryIds = [...new Set(limitedPmsData.map(row => row.subsidiaryId))]

      const batchSize = 10
      const campaigns = new Map()

      for (let i = 0; i < subsidiaryIds.length; i += batchSize) {
        const batch = subsidiaryIds.slice(i, i + batchSize)

        try {
          const results = await Promise.allSettled(
            batch.map(subsidiaryId => campaignApi.getList(subsidiaryId))
          )

          results.forEach((result, index) => {
            const subsidiaryId = batch[index]
            
            if (result.status === 'fulfilled' && result.value) {
              const items = result.value.items || []
              campaigns.set(subsidiaryId, items)
            } else if (result.status === 'rejected') {
              console.warn(
                `Failed to fetch campaigns for subsidiary ${batch[index]}:`,
                result.reason
              )
            }
          })
        } catch (error) {
          console.warn('Error fetching campaigns batch:', error)
        }
      }

      setCampaignsMap(campaigns)
      setIsLoadingCampaigns(false)
    }

    fetchCampaigns()
  }, [pmsData])

  // Fetch custom campaigns (drip) data
  useEffect(() => {
    if (!pmsData || !Array.isArray(pmsData) || pmsData.length === 0) {
      setCustomCampaignsCountMap(new Map())
      return
    }

    const fetchCustomCampaigns = async () => {
      try {
        // Get subsidiary IDs we're interested in
        const limitedPmsData = pmsData.slice(0, 200)
        const targetSubsidiaryIds = new Set(limitedPmsData.map(row => row.subsidiaryId))

        // Fetch all custom campaigns for the current user
        const customCampaignsResponse = await campaignApi.getCustomCampaignList()
        const allCustomCampaigns = customCampaignsResponse?.items || []
        
        if (allCustomCampaigns.length === 0) {
          setCustomCampaignsCountMap(new Map())
          return
        }

        // Get unique audience IDs from custom campaigns
        const audienceIds = [...new Set(allCustomCampaigns.map(c => c.audienceId).filter(Boolean))]

        // Fetch all audiences list to create audienceId -> subsidiaryId mapping
        const audiencesResponse = await audienceApi.getList(0, 1000) // skip=0, take=1000
        const allAudiences = audiencesResponse?.items || []
        
        // Create map: audienceId -> subsidiaryId
        const audienceToSubsidiaryMap = new Map()
        allAudiences.forEach(audience => {
          if (audience.id && audience.subsidiaryId) {
            audienceToSubsidiaryMap.set(audience.id, audience.subsidiaryId)
          }
        })

        // Group custom campaigns by subsidiaryId and count active ones (excluding deleted)
        const customCounts = new Map()
        
        // Initialize all target subsidiaries with 0
        targetSubsidiaryIds.forEach(subsidiaryId => {
          customCounts.set(subsidiaryId, 0)
        })
        
        allCustomCampaigns.forEach(campaign => {
          const subsidiaryId = audienceToSubsidiaryMap.get(campaign.audienceId)
          
          if (subsidiaryId && targetSubsidiaryIds.has(subsidiaryId)) {
            // Filter out deleted campaigns
            if (campaign.customDeliveryStatus !== 'Deleted') {
              const currentCount = customCounts.get(subsidiaryId) || 0
              customCounts.set(subsidiaryId, currentCount + 1)
            }
          }
        })
        
        setCustomCampaignsCountMap(customCounts)
      } catch (error) {
        console.error('Error fetching custom campaigns:', error)
        setCustomCampaignsCountMap(new Map())
      }
    }

    fetchCustomCampaigns()
  }, [pmsData])

  function handleTitleCellClick(rowIndex) {
    if (sortByIndex === rowIndex) {
      setSortDecreasing(d => !d)
    }
    setSortByIndex(rowIndex)
  }

  // Combine data from all three sources using useMemo for performance
  const results = useMemo(() => {
    // Early returns if data is not ready
    if (isLoading) return []
    if (!pmsData || !Array.isArray(pmsData) || pmsData.length === 0) return []
    if (!billingData || !Array.isArray(billingData)) return []
    if (!invitesData || !Array.isArray(invitesData)) return []
    if (!performanceData || !Array.isArray(performanceData)) return []

    // Don't process if feature data is still loading (optional - can remove if you want to show partial data)
    // if (isLoadingFeatures) return []

    // Limit initial processing to first 500 hotels for performance
    const limitedPmsData = pmsData.slice(0, 500)

    // Create maps for quick lookup (only for limited data)
    const billingMap = new Map(billingData.map(b => [b.subsidiaryId, b]))
    const invitesMap = new Map(invitesData.map(i => [i.subsidiaryId, i]))
    const performanceMap = new Map(performanceData.map(p => [p.subsidiaryId, p]))

    // Combine data (only for limited set)
    const combinedData = limitedPmsData.map(pms => {
      const billing = billingMap.get(pms.subsidiaryId) || {}
      const invites = invitesMap.get(pms.subsidiaryId) || {}
      const performance = performanceMap.get(pms.subsidiaryId) || {}

      // Count outstanding invoices (PaymentPending, PaymentOverdue, PaymentFailed, PaidPartially)
      let outstandingInvoiceCount = 0
      if (performance.items && typeof performance.items === 'object') {
        const outstandingStatuses = [
          'PaymentPending',
          'PaymentOverdue',
          'PaymentFailed',
          'PaidPartially'
        ]
        const items = performance.items
        outstandingInvoiceCount = Object.values(items).filter(
          item => item && item.status && outstandingStatuses.includes(item.status)
        ).length
      }

      // Get actualServiceStartDate and campaigns from map
      const featureData = featureDataMap.get(pms.subsidiaryId) || {}
      const actualServiceStartDate = featureData.actualServiceStartDate || null
      const campaigns = featureData.campaigns || campaignsMap.get(pms.subsidiaryId) || []
      const customCampaignsCount = customCampaignsCountMap.get(pms.subsidiaryId) || 0

      const rowData = {
        ...pms,
        // Billing data
        isAutoPayEnabled: billing.isAutoPayEnabled || false,
        balanceCurrent: billing.balanceCurrent || 0,
        balanceLate: billing.balanceLate || 0,
        hasDripCampaign: billing.hasDripCampaign || billing.HasDripCampaign || false,
        customCampaignsCount,
        paymentMethod: billing.paymentMethod,
        paymentPeriod: billing.paymentPeriod,
        packagePlans: billing.packagePlans,
        packageFee: billing.packageFee || 0,
        discount: billing.discount || 0,
        monthlyFeeAfterDiscount: billing.monthlyFeeAfterDiscount || 0,
        // Invite data
        marketingCampaigns: invites.marketingCampaigns || [],
        invited: invites.invited || {},
        clicks: invites.clicks || {},
        bookings: invites.bookings || {},
        conversions: invites.conversions || {},
        // Performance data
        outstandingInvoiceCount,
        serviceStartDate: performance.serviceStartDate,
        actualServiceStartDate,
        campaigns
      }

      // Calculate row status
      rowData.rowStatus = calculateRowStatus(rowData)

      return rowData
    })

    // Apply filters
    const { searchString, chains, brands, hotelGroups, subsidiaryStatus, pms, csmIds } =
      filterState

    const filtered = combinedData
      .filter(h => {
        if (!h.searchKey) return false
        try {
          return h.searchKey.match(new RegExp(searchString || '', 'i'))
        } catch {
          return true
        }
      })
      .filter(h => (hotelGroups === 'all' ? true : h.groupName === hotelGroups))
      .filter(h => (chains === 'all' ? true : h.chainCode === chains))
      .filter(h => (brands === 'all' ? true : h.parentChainCode === brands))
      .filter(h => {
        if (!pms || pms.length === 0) return true
        return pms.includes(h.pms)
      })
      .filter(h => {
        if (!subsidiaryStatus || subsidiaryStatus.length === 0) return true
        if (hotelCampaignRegistrationStatuses.includes(h.subsidiaryStatus)) {
          return subsidiaryStatus.includes('PendingCampaignBrandRegistration')
        }
        return subsidiaryStatus.includes(h.subsidiaryStatus)
      })
      .filter(h => {
        if (!csmIds || csmIds.length === 0) return true
        return csmIds.includes(`${h.csmUserId}`)
      })
      .filter(h => {
        // Exclude hotels in HotelConfigurationStage
        if (h.subsidiaryStatus === 'HotelConfigurationStage') {
          return false
        }
        return true
      })
      .filter(h => {
        // Filter by alert status
        if (!h.rowStatus) return true
        if (h.rowStatus === 'alert' && !showAlert) return false
        if (h.rowStatus === 'warning' && !showWarning) return false
        if (h.rowStatus === 'green' && !showGreen) return false
        return true
      })

    // Sort results
    const titleRowNames = [
      'displayId',
      'shortName',
      'csmUserFullName',
      'pms',
      'isAutoPayEnabled',
      'balanceLate',
      null, // 3 Mo (not sortable)
      null, // 6 Mo (not sortable)
      null, // 9 Mo (not sortable)
      null, // 12 Mo (not sortable)
      null, // 15 Mo (not sortable)
      null, // 15 Mo+ (not sortable)
      null, // After visit (not sortable)
      'invited',
      'conversions',
      'balanceLate',
      'outstandingInvoiceCount',
      'hasDripCampaign',
      null // Notes (not sortable)
    ]

    const sortByProp = titleRowNames[sortByIndex]

    const sortedData = [...filtered].sort((a, b) => {
      if (!sortByProp) return 0 // Don't sort campaign columns

      if (sortByProp === 'csmUserFullName' || sortByProp === 'pms') {
        const aVal = (a[sortByProp] || '').toString()
        const bVal = (b[sortByProp] || '').toString()
        if (aVal === bVal) return 0
        return aVal.localeCompare(bVal)
      }
      if (sortByProp === 'invited' || sortByProp === 'conversions') {
        // Sort by periodCurrent value
        const aVal =
          a[sortByProp]?.periodCurrent != null ? a[sortByProp].periodCurrent : 0
        const bVal =
          b[sortByProp]?.periodCurrent != null ? b[sortByProp].periodCurrent : 0
        return bVal - aVal
      }
      if (sortByProp === 'outstandingInvoiceCount' || sortByProp === 'balanceLate') {
        // Sort by count or amount
        const aVal = a[sortByProp] != null ? a[sortByProp] : 0
        const bVal = b[sortByProp] != null ? b[sortByProp] : 0
        return bVal - aVal
      }
      if (sortByProp === 'hasDripCampaign' || sortByProp === 'isAutoPayEnabled') {
        // Sort by boolean (true first)
        const aVal = a[sortByProp] === true ? 1 : 0
        const bVal = b[sortByProp] === true ? 1 : 0
        return bVal - aVal
      }
      if (typeof a[sortByProp] === 'string') {
        return (a[sortByProp] || '').localeCompare(b[sortByProp] || '')
      } else if (typeof a[sortByProp] === 'boolean') {
        return Number(b[sortByProp]) - Number(a[sortByProp])
      } else {
        return (b[sortByProp] || 0) - (a[sortByProp] || 0)
      }
    })

    const sorted = sortDecreasing ? sortedData : sortedData.reverse()

    // Limit the number of rows displayed for performance
    return sorted.slice(0, displayLimit)
  }, [
    isLoading,
    pmsData,
    billingData,
    invitesData,
    performanceData,
    filterState.searchString,
    filterState.chains,
    filterState.brands,
    filterState.hotelGroups,
    filterState.subsidiaryStatus,
    filterState.pms,
    filterState.csmIds,
    sortByIndex,
    sortDecreasing,
    displayLimit,
    showAlert,
    showWarning,
    showGreen,
    featureDataMap,
    campaignsMap,
    customCampaignsCountMap
  ])

  // Calculate total results count (simplified - just use filtered length)
  const totalResultsCount =
    results.length >= displayLimit ? displayLimit + 1 : results.length

  // Calculate summary counts by status
  const statusCounts = useMemo(() => {
    if (isLoading || !pmsData || !billingData || !invitesData || !performanceData) {
      return { alert: 0, warning: 0, green: 0, total: 0 }
    }

    // Create maps for quick lookup
    const billingMapForCounts = new Map(billingData.map(b => [b.subsidiaryId, b]))
    const invitesMapForCounts = new Map(invitesData.map(i => [i.subsidiaryId, i]))
    const performanceMapForCounts = new Map(performanceData.map(p => [p.subsidiaryId, p]))

    // We need to recalculate statuses for all data, not just displayed
    const allCombinedData = pmsData.map(pms => {
      const billing = billingMapForCounts.get(pms.subsidiaryId) || {}
      const invites = invitesMapForCounts.get(pms.subsidiaryId) || {}
      const performance = performanceMapForCounts.get(pms.subsidiaryId) || {}

      let outstandingInvoiceCount = 0
      if (performance.items && typeof performance.items === 'object') {
        const outstandingStatuses = [
          'PaymentPending',
          'PaymentOverdue',
          'PaymentFailed',
          'PaidPartially'
        ]
        const items = performance.items
        outstandingInvoiceCount = Object.values(items).filter(
          item => item && item.status && outstandingStatuses.includes(item.status)
        ).length
      }

      const rowData = {
        ...pms,
        isAutoPayEnabled: billing.isAutoPayEnabled || false,
        balanceCurrent: billing.balanceCurrent || 0,
        balanceLate: billing.balanceLate || 0,
        hasDripCampaign: billing.hasDripCampaign || billing.HasDripCampaign || false,
        marketingCampaigns: invites.marketingCampaigns || [],
        invited: invites.invited || {},
        outstandingInvoiceCount,
        serviceStartDate: performance.serviceStartDate
      }

      rowData.rowStatus = calculateRowStatus(rowData)
      return rowData
    })

    // Apply same filters (except alert status filters) to get accurate counts
    const { searchString, chains, brands, hotelGroups, subsidiaryStatus, pms, csmIds } =
      filterState

    const filtered = allCombinedData
      .filter(h => {
        if (!h.searchKey) return false
        try {
          return h.searchKey.match(new RegExp(searchString || '', 'i'))
        } catch {
          return true
        }
      })
      .filter(h => (hotelGroups === 'all' ? true : h.groupName === hotelGroups))
      .filter(h => (chains === 'all' ? true : h.chainCode === chains))
      .filter(h => (brands === 'all' ? true : h.parentChainCode === brands))
      .filter(h => {
        if (!pms || pms.length === 0) return true
        return pms.includes(h.pms)
      })
      .filter(h => {
        if (!subsidiaryStatus || subsidiaryStatus.length === 0) return true
        if (hotelCampaignRegistrationStatuses.includes(h.subsidiaryStatus)) {
          return subsidiaryStatus.includes('PendingCampaignBrandRegistration')
        }
        return subsidiaryStatus.includes(h.subsidiaryStatus)
      })
      .filter(h => {
        if (!csmIds || csmIds.length === 0) return true
        return csmIds.includes(`${h.csmUserId}`)
      })
      .filter(h => {
        if (h.subsidiaryStatus === 'HotelConfigurationStage') {
          return false
        }
        return true
      })

    const counts = { alert: 0, warning: 0, green: 0, total: filtered.length }
    filtered.forEach(h => {
      if (h.rowStatus === 'alert') counts.alert++
      else if (h.rowStatus === 'warning') counts.warning++
      else if (h.rowStatus === 'green') counts.green++
    })

    return counts
  }, [isLoading, pmsData, billingData, invitesData, performanceData, filterState])

  // Prepare subsidiary IDs for batch loading (only for displayed results)
  const subsidiaryIdsForBatchLoading = useMemo(() => {
    return results.map(h => ({
      subsidiaryId: h.subsidiaryId,
      kind: 'healthCheck-summary'
    }))
  }, [results])

  function formatPrice(number) {
    return parseFloat(number || 0).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    })
  }

  // Get notes cache for CSV export
  const SummaryContent = () => {
    const { notesCache } = useNoteBatchLoader()

    function handleClickDownloadAsCsv() {
      // Get filter values for metadata
      const {
        searchString,
        chains,
        brands,
        hotelGroups,
        subsidiaryStatus,
        csmIds
      } = filterState

      // Helper function to get filter label
      const getFilterLabel = (value, allOptions, defaultLabel) => {
        if (value === 'all' || !value || (Array.isArray(value) && value.length === 0)) {
          return 'All'
        }
        if (Array.isArray(value)) {
          return value.map(v => allOptions[v] || v).join(', ')
        }
        return allOptions[value] || value || defaultLabel
      }

      // Get CSM names if available
      const getCsmLabel = () => {
        if (!csmIds || csmIds.length === 0) return 'All'
        return csmIds.join(', ')
      }

      // Get status labels
      const getStatusLabel = () => {
        if (!subsidiaryStatus || subsidiaryStatus.length === 0) return 'All'
        return subsidiaryStatus.map(s => hotelStatusNamesFilterable[s] || s).join(', ')
      }

      const filterMetadata = [
        ['Filter Values Applied:'],
        ['Hotel Group', getFilterLabel(hotelGroups, allhotelGroups, 'All')],
        ['Brand', getFilterLabel(brands, parentChainNames, 'All')],
        ['Chain', getFilterLabel(chains, allhotelChains, 'All')],
        ['Status', getStatusLabel()],
        ['CSM', getCsmLabel()],
        ['Search', searchString || 'None'],
        ['Status Filters', `Alert: ${showAlert ? 'Yes' : 'No'}, Warning: ${showWarning ? 'Yes' : 'No'}, Green: ${showGreen ? 'Yes' : 'No'}`],
        [] // Empty row separator
      ]

      const titleRow = [
        'Hotel ID',
        'Hotel Name',
        'Hotel Group',
        'Brand',
        'Chain',
        'CSM',
        'Status',
        'PMS',
        'Autopay',
        '3 Mo',
        '6 Mo',
        '9 Mo',
        '12 Mo',
        '15 Mo',
        '15 Mo+',
        'Sales',
        'Drip',
        'Service',
        'After visit',
        'Text',
        'Converted',
        'Late balance',
        'Outstanding Invoices',
        'Payment Method',
        'Billing Frequency',
        'Package Plans',
        'Package Fee',
        'Discount',
        'Monthly Fee (After Discount)',
        'Service Start Date',
        'Date Added',
        'Notes'
      ]

      const resAsArrays = results.map(h => {
        // Get note from cache if available
        const noteKey = `${h.subsidiaryId}-healthCheck-summary`
        const note = notesCache?.get(noteKey) || '-'

        return [
          h.displayId,
          `${h.brand || ''} ${h.shortName || ''}`.trim(),
          h.groupName || '-',
          h.parentChainCode || '-',
          h.chainCode || '-',
          h.csmUserFullName || '-',
          h.subsidiaryStatus ? (hotelStatusNamesFilterable[h.subsidiaryStatus] || h.subsidiaryStatus) : '-',
          h.pms ? (PMSnames[h.pms] || h.pms) : '-',
          h.isAutoPayEnabled ? 'Yes' : 'No',
          h.marketingCampaigns?.find(c => c.category === 'Lost3Months')?.isActive ? 'Yes' : 'No',
          h.marketingCampaigns?.find(c => c.category === 'Lost6Months')?.isActive ? 'Yes' : 'No',
          h.marketingCampaigns?.find(c => c.category === 'Lost9Months')?.isActive ? 'Yes' : 'No',
          h.marketingCampaigns?.find(c => c.category === 'Lost12Months')?.isActive ? 'Yes' : 'No',
          h.marketingCampaigns?.find(c => c.category === 'Lost15Months')?.isActive ? 'Yes' : 'No',
          h.marketingCampaigns?.find(c => c.category === 'Lost15MonthsPlus')?.isActive ? 'Yes' : 'No',
          h.campaigns?.filter(c => c.kind === 'Sales' && c.isActive).length || 0,
          h.customCampaignsCount || 0,
          h.campaigns?.filter(c => c.kind === 'Service' && c.isActive).length || 0,
          h.marketingCampaigns?.find(c => c.category === 'AfterLastVisit')?.isActive ? 'Yes' : 'No',
          h.invited?.periodCurrent || 0,
          h.conversions?.periodCurrent || 0,
          h.balanceLate || 0,
          h.outstandingInvoiceCount || 0,
          h.paymentMethod || '-',
          h.paymentPeriod || '-',
          h.packagePlans || '-',
          formatPrice(h.packageFee),
          formatPrice(h.discount),
          formatPrice(h.monthlyFeeAfterDiscount),
          h.serviceStartDate
            ? moment(h.serviceStartDate).format('MM/DD/YYYY')
            : '-',
          h.addedOn
            ? moment(h.addedOn).format('MM/DD/YYYY')
            : '-',
          note
        ]
      })

      // Combine metadata, title row, and data
      const dataArray = [
        ...filterMetadata,
        titleRow,
        ...resAsArrays
      ]
      
      const fileName = `health_check_summary_${moment().format('YYYY-MM-DD')}.csv`

      downloadAsCsv(dataArray, fileName)
    }

    return (
      <>
        <HealthCheckFilters
          state={filterState}
          dispatch={filterDispatch}
          allhotelGroups={allhotelGroups}
          allhotelChains={allhotelChains}
          showSubsidiaryStatus
          showPMSFilter
          showCsmFilter
        >
          <div className={styles.alertStatusControls}>
            <span className={styles.alertStatusControlsTitle}>Filter by Status:</span>
            <Toggle
              className={styles.toggle}
              checked={showAlert}
              handleChange={e => filterDispatch({ type: 'showAlert', payload: e.target.checked })}
              label="Alert"
            />
            <Toggle
              className={styles.toggle}
              checked={showWarning}
              handleChange={e => filterDispatch({ type: 'showWarning', payload: e.target.checked })}
              label="Warning"
            />
            <Toggle
              className={styles.toggle}
              checked={showGreen}
              handleChange={e => filterDispatch({ type: 'showGreen', payload: e.target.checked })}
              label="Green"
            />
          </div>
          <SecondaryRegularButton
            className={styles.copyToExcelButton}
            onClick={handleClickDownloadAsCsv}
          >
            <ExcelIcon /> Download as CSV
          </SecondaryRegularButton>
        </HealthCheckFilters>

        <div className={styles.summaryStats}>
          <div className={styles.summaryStat}>
            <span className={styles.summaryStatLabel}>Total:</span>
            <span className={styles.summaryStatValue}>{statusCounts.total}</span>
          </div>
          <div className={styles.summaryStat}>
            <span className={styles.summaryStatLabel}>Alert:</span>
            <span className={`${styles.summaryStatValue} ${styles.alertColor}`}>
              {statusCounts.alert}
            </span>
          </div>
          <div className={styles.summaryStat}>
            <span className={styles.summaryStatLabel}>Warning:</span>
            <span className={`${styles.summaryStatValue} ${styles.warningColor}`}>
              {statusCounts.warning}
            </span>
          </div>
          <div className={styles.summaryStat}>
            <span className={styles.summaryStatLabel}>Green:</span>
            <span className={`${styles.summaryStatValue} ${styles.greenColor}`}>
              {statusCounts.green}
            </span>
          </div>
        </div>
        <div className={styles.tableContainer}>
          {isLoading ? (
            <LoadingIndicator marginTop={100} />
          ) : (
            <Table
              widths={[
                50, 300, 80, 80, 60, 60, 60, 60, 60, 60, 60, 80, 80, 80, 80, 80, 60, 80, 95,
                160
              ]}
              handleTitleCellClick={handleTitleCellClick}
              sortIndex={sortByIndex}
              sortDirection={sortDecreasing ? 'down' : 'up'}
            >
              <SummaryTitleRow />
              {results.length === 0 && totalResultsCount === 0 && (
                <div className={styles.noResults}>
                  No matching results. Try modifying the filters.
                </div>
              )}
              {results.map((h, i) => {
                const statusExplanation = getStatusExplanation(h)
                return (
                  <SummaryRow
                    {...h}
                    key={h.subsidiaryId}
                    index={i + 1}
                    statusExplanation={statusExplanation}
                  />
                )
              })}
              {totalResultsCount > displayLimit && (
                <div className={styles.loadMoreContainer}>
                  <div className={styles.loadMoreInfo}>
                    Showing {displayLimit} of {totalResultsCount} results
                  </div>
                  <button
                    className={styles.loadMoreButton}
                    onClick={() => setDisplayLimit(prev => prev + 100)}
                  >
                    Load More (100)
                  </button>
                </div>
              )}
            </Table>
          )}
        </div>
      </>
    )
  }

  function handleClickDownloadAsCsv() {
    // This function is now handled by SummaryContent component
  }

  return (
    <NoteBatchLoaderProvider
      subsidiaryIds={subsidiaryIdsForBatchLoading}
      kind="healthCheck-summary"
    >
      <div className={styles.wrapper}>
        <CustomHeader title="Hotel health check" width={1800} isFixed />
        <Tab.Parent>
          <Tab to="/healthCheck/pms">PMS</Tab>
          <Tab to="/healthCheck/autoinvite">AutoInvite</Tab>
          <Tab to="/healthCheck/billing">Billing</Tab>
          <Tab to="/healthCheck/performance">Performance</Tab>
          <Tab to="/healthCheck/summary">Summary</Tab>
        </Tab.Parent>
        <SummaryContent />
    </div>
    </NoteBatchLoaderProvider>
  )
}

export default HealthCheckSummary
